import { useState, type ReactNode } from 'react';
import { Button, Icon, InlineStack, PlainButton, Stack, TextArea, TextField } from '@customerio/pluma-components/react';
import { capitalize, isEmptyString } from '@/utils/string';
import * as styles from './inline-edit-text.css';

type InlineEditTextProps = {
  value: string;
  /** What is being edited ("name"), for the edit button's label and the field's. */
  label: string;
  placeholder?: string;
  isMultiline?: boolean;
  /** Whether a blank value can be saved: a description can, a name cannot. */
  allowsEmpty?: boolean;
  isDisabled: boolean;
  /** Saves the value; resolves to whether it went through, so a failure keeps the editor open. */
  onSave: (value: string) => Promise<boolean>;
  /** The read view. */
  children: ReactNode;
};

/**
 * Text edited in place, as Ember's `InlineEdit` did: the read view with a pencil that appears on
 * hover, then a field with Save and Cancel. Enter saves a single line; Escape cancels either.
 */
export function InlineEditText({
  value,
  label,
  placeholder,
  isMultiline = false,
  allowsEmpty = false,
  isDisabled,
  onSave,
  children,
}: InlineEditTextProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedValue, setEditedValue] = useState(value);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const startEditing = () => {
    setEditedValue(value);
    setError(undefined);
    setIsEditing(true);
  };
  const cancel = () => setIsEditing(false);
  const save = async () => {
    if (isSaving) {
      return;
    }
    // Save stays enabled so the click can say what is wrong.
    if (!allowsEmpty && isEmptyString(editedValue)) {
      setError(`${capitalize(label)} can't be blank`);
      return;
    }
    if (editedValue === value) {
      cancel();
      return;
    }
    setIsSaving(true);
    const saved = await onSave(editedValue);
    setIsSaving(false);
    if (saved) {
      setIsEditing(false);
    }
  };
  // Typed structurally: Pluma's TextField hands React's event and TextArea the DOM's.
  const onKeyDown = (event: { key: string; preventDefault: () => void }) => {
    if (event.key === 'Escape') {
      cancel();
    }
    if (event.key === 'Enter' && !isMultiline) {
      event.preventDefault();
      void save();
    }
  };

  if (isDisabled) {
    return <>{children}</>;
  }

  if (isEditing) {
    return (
      <Stack gap="100" data-test-inline-edit-form={label}>
        {isMultiline ? (
          <TextArea
            ariaLabel={label}
            autoFocus={true}
            value={editedValue}
            placeholder={placeholder}
            isDisabled={isSaving}
            onInput={(event) => setEditedValue(event.target.value)}
            onKeyDown={onKeyDown}
          />
        ) : (
          <TextField
            ariaLabel={label}
            autoFocus={true}
            autoComplete="off"
            value={editedValue}
            placeholder={placeholder}
            error={error}
            isDisabled={isSaving}
            onInput={(event) => {
              setError(undefined);
              setEditedValue(event.target.value);
            }}
            onKeyDown={onKeyDown}
          />
        )}
        <InlineStack gap="100">
          <Button variant="primary" size="sm" isLoading={isSaving} onClick={() => void save()}>
            Save
          </Button>
          <Button variant="secondary" size="sm" isDisabled={isSaving} onClick={cancel}>
            Cancel
          </Button>
        </InlineStack>
      </Stack>
    );
  }

  return (
    <InlineStack gap="100" alignBlock="center" className={styles.row} data-test-inline-edit={label}>
      {children}
      <PlainButton className={styles.editAffordance} aria-label={`Edit ${label}`} onClick={startEditing}>
        <Icon name="edit" color="subtle" size="sm" />
      </PlainButton>
    </InlineStack>
  );
}
